import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Loader2, Plus, Pencil, Trash2, X, Save, Image as ImageIcon, Copy, ExternalLink } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";
import { ImageUploader, MediaLibraryModal } from "@/admin/components/ImageUploader";
import { resolveImageUrl } from "@/lib/media";

// Fields that should render an ImageUploader instead of a plain text input.
const IMAGE_FIELD_NAMES = new Set(["image", "photo", "cover_image", "logo", "thumbnail", "avatar"]);

// Suggested crop aspect ratio per (entity, field). Falls back to "free".
const DEFAULT_ASPECT = {
  news: { image: "16:9" },
  doctors: { photo: "1:1" },
  gallery: { image: "4:3" },
  hero_slides: { image: "16:9" },
  about_content: { image: "4:5" },
};

// Curated list of Lucide icon names most useful for hospital stat cards.
// User can also type any other valid Lucide icon name — free-text input remains available.
const ICON_SUGGESTIONS = [
  "Users", "UserRound", "Stethoscope", "HeartPulse", "Heart", "Activity",
  "Clock", "Shield", "ShieldCheck", "BedDouble", "Pill", "Syringe",
  "Hospital", "Cross", "Building2", "Baby", "Brain", "Bone",
  "Microscope", "TestTube", "Ambulance", "Award", "Sparkles", "Star",
  "Phone", "PhoneCall", "MapPin", "Calendar", "TrendingUp", "BarChart3",
];

const ENTITIES = {
  services: {
    label: "Layanan",
    fields: [
      { name: "icon", label: "Icon (Lucide name)", placeholder: "Stethoscope" },
      { name: "title_id", label: "Judul (ID)" },
      { name: "title_en", label: "Judul (EN)" },
      { name: "description_id", label: "Deskripsi (ID)", type: "textarea" },
      { name: "description_en", label: "Deskripsi (EN)", type: "textarea" },
    ],
    display: (i) => i.title_id,
  },
  doctors: {
    label: "Dokter",
    fields: [
      { name: "name", label: "Nama Lengkap" },
      { name: "specialization_id", label: "Spesialisasi (ID)" },
      { name: "specialization_en", label: "Spesialisasi (EN)" },
      { name: "schedule_id", label: "Jadwal (ID)" },
      { name: "schedule_en", label: "Jadwal (EN)" },
      { name: "photo", label: "URL Foto" },
    ],
    display: (i) => i.name,
  },
  news: {
    label: "Berita",
    fields: [
      { name: "title_id", label: "Judul (ID)" },
      { name: "title_en", label: "Judul (EN)" },
      { name: "excerpt_id", label: "Ringkasan (ID)", type: "textarea" },
      { name: "excerpt_en", label: "Ringkasan (EN)", type: "textarea" },
      { name: "image", label: "URL Gambar" },
      { name: "category_id", label: "Kategori (ID)" },
      { name: "category_en", label: "Kategori (EN)" },
      { name: "date", label: "Tanggal (YYYY-MM-DD)", placeholder: "2025-12-15" },
    ],
    display: (i) => i.title_id,
  },
  gallery: {
    label: "Galeri",
    fields: [
      { name: "image", label: "URL Gambar" },
      { name: "caption_id", label: "Caption (ID)" },
      { name: "caption_en", label: "Caption (EN)" },
    ],
    display: (i) => i.caption_id,
  },
  hero_slides: {
    label: "Slide Hero",
    endpoint: "hero_slides",
    fields: [
      { name: "order", label: "Urutan (angka, 1 tampil pertama)", type: "number", placeholder: "1" },
      { name: "active", label: "Aktif (tampilkan di homepage)", type: "checkbox" },
      { name: "image", label: "URL Gambar (1800×1000+ direkomendasikan)" },
      { name: "badge_id", label: "Badge kecil (ID)", optional: true, placeholder: "PELAYANAN KESEHATAN TERPADU" },
      { name: "badge_en", label: "Badge kecil (EN)", optional: true, placeholder: "INTEGRATED HEALTHCARE" },
      { name: "title_line1_id", label: "Judul baris 1 (ID)", placeholder: "Rumah Sakit" },
      { name: "title_line1_en", label: "Judul baris 1 (EN)", placeholder: "A Trusted Hospital" },
      { name: "title_line2_id", label: "Judul baris 2 — biru (ID)", optional: true, placeholder: "Terpercaya di Tarakan" },
      { name: "title_line2_en", label: "Judul baris 2 — biru (EN)", optional: true, placeholder: "in Tarakan" },
      { name: "subtitle_id", label: "Subjudul (ID)", type: "textarea", optional: true },
      { name: "subtitle_en", label: "Subjudul (EN)", type: "textarea", optional: true },
      { name: "cta_primary_text_id", label: "Tombol utama teks (ID)", optional: true, placeholder: "Buat Janji Temu" },
      { name: "cta_primary_text_en", label: "Tombol utama teks (EN)", optional: true, placeholder: "Book Appointment" },
      { name: "cta_primary_url", label: "Tombol utama URL (kosong = WhatsApp)", optional: true, placeholder: "https://…" },
      { name: "cta_secondary_text_id", label: "Tombol kedua teks (ID)", optional: true, placeholder: "Lihat Layanan" },
      { name: "cta_secondary_text_en", label: "Tombol kedua teks (EN)", optional: true, placeholder: "See Services" },
      { name: "cta_secondary_url", label: "Tombol kedua URL", optional: true, placeholder: "#services" },
      { name: "caption_id", label: "Caption bawah slide (ID)", optional: true },
      { name: "caption_en", label: "Caption bawah slide (EN)", optional: true },
    ],
    display: (i) => `${i.order ?? "-"}. ${i.title_line1_id || i.badge_id || "Slide"}`,
  },
  hero_stats: {
    label: "Stat Hero (kartu statistik di bawah carousel)",
    endpoint: "hero_stats",
    fields: [
      { name: "order", label: "Urutan (1-4)", type: "number", placeholder: "1" },
      { name: "active", label: "Aktif (tampilkan)", type: "checkbox" },
      {
        name: "icon",
        label: "Icon (nama Lucide)",
        type: "icon",
        placeholder: "Users",
      },
      { name: "value", label: "Angka / Simbol (mis: 20+, 24/7, 1.5k)", placeholder: "20+" },
      { name: "label_id", label: "Label (ID)", placeholder: "Dokter Spesialis" },
      { name: "label_en", label: "Label (EN)", placeholder: "Specialist Doctors" },
    ],
    display: (i) => `${i.order ?? "-"}. ${i.value} — ${i.label_id}`,
  },
  about_content: {
    label: "Tentang Kami — Konten",
    endpoint: "about_content",
    fields: [
      { name: "image", label: "URL Gambar (potret, 4:5)", placeholder: "https://..." },
      { name: "overline_id", label: "Overline (ID)", optional: true, placeholder: "TENTANG KAMI" },
      { name: "overline_en", label: "Overline (EN)", optional: true, placeholder: "ABOUT US" },
      { name: "title_id", label: "Judul (ID)", placeholder: "Rumah Sakit Terdepan…" },
      { name: "title_en", label: "Judul (EN)", placeholder: "A Leading Hospital…" },
      { name: "p1_id", label: "Paragraf 1 (ID)", type: "textarea", optional: true },
      { name: "p1_en", label: "Paragraf 1 (EN)", type: "textarea", optional: true },
      { name: "p2_id", label: "Paragraf 2 (ID)", type: "textarea", optional: true },
      { name: "p2_en", label: "Paragraf 2 (EN)", type: "textarea", optional: true },
      { name: "badge_value", label: "Badge — angka (mis: 25+)", optional: true, placeholder: "25+" },
      { name: "badge_label_id", label: "Badge — label (ID)", optional: true, placeholder: "Tahun Pengalaman" },
      { name: "badge_label_en", label: "Badge — label (EN)", optional: true, placeholder: "Years of Experience" },
    ],
    display: (i) => i.title_id || "About content",
  },
  about_pillars: {
    label: "Tentang Kami — Pilar (Visi / Misi / Nilai)",
    endpoint: "about_pillars",
    fields: [
      { name: "order", label: "Urutan", type: "number", placeholder: "1" },
      { name: "active", label: "Aktif (tampilkan)", type: "checkbox" },
      { name: "icon", label: "Icon (nama Lucide)", type: "icon", placeholder: "Compass" },
      { name: "label_id", label: "Label (ID)", placeholder: "Visi" },
      { name: "label_en", label: "Label (EN)", placeholder: "Vision" },
      { name: "text_id", label: "Teks (ID)", type: "textarea" },
      { name: "text_en", label: "Teks (EN)", type: "textarea" },
    ],
    display: (i) => `${i.order ?? "-"}. ${i.label_id}`,
  },
};

const emptyForm = (entity) => {
  const out = {};
  for (const f of ENTITIES[entity].fields) {
    if (f.type === "checkbox") out[f.name] = f.default ?? true;
    else if (f.type === "number") out[f.name] = f.default ?? 0;
    else out[f.name] = f.default ?? "";
  }
  return out;
};

const CrudPanel = ({ entity }) => {
  const cfg = ENTITIES[entity];
  const endpoint = cfg.endpoint || entity;
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {} = create, obj = edit
  const [form, setForm] = useState(emptyForm(entity));
  const [saving, setSaving] = useState(false);
  const [ogPreview, setOgPreview] = useState(null); // news item currently previewed
  const [libraryFor, setLibraryFor] = useState(null); // field name currently opening library picker

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [entity]);

  const openCreate = () => {
    setForm(emptyForm(entity));
    setEditing({ _new: true });
  };

  const openEdit = (item) => {
    setForm({ ...emptyForm(entity), ...item });
    setEditing(item);
  };

  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Coerce numeric fields
      const payload = { ...form };
      for (const f of cfg.fields) {
        if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) {
          payload[f.name] = Number(payload[f.name]);
        }
      }
      if (editing._new) {
        await authAxios.post(`/admin/${endpoint}`, payload);
        toast.success(`${cfg.label} berhasil ditambahkan`);
      } else {
        await authAxios.put(`/admin/${endpoint}/${editing.id}`, payload);
        toast.success(`${cfg.label} berhasil diperbarui`);
      }
      close();
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    try {
      await authAxios.delete(`/admin/${endpoint}/${id}`);
      toast.success("Item dihapus");
      load();
    } catch (ex) {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div data-testid={`admin-panel-${entity}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Kelola {cfg.label}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} item tersimpan
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid={`admin-${entity}-add`}
          className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-4 py-2.5 rounded-full text-sm font-medium shadow-md shadow-blue-900/20"
        >
          <Plus className="h-4 w-4" />
          Tambah
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`admin-${entity}-item-${item.id}`}
              className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:border-blue-100 transition"
            >
              {item.image || item.photo ? (
                <img
                  src={resolveImageUrl(item.image || item.photo)}
                  alt=""
                  className="w-full h-32 object-cover rounded-xl mb-3"
                />
              ) : null}
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                {item.id}
              </div>
              <div className="font-heading font-semibold text-slate-900 mb-4 line-clamp-2">
                {cfg.display(item)}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => openEdit(item)}
                  data-testid={`admin-${entity}-edit-${item.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 hover:bg-blue-100"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                {entity === "news" && (
                  <button
                    onClick={() => setOgPreview(item)}
                    data-testid={`admin-news-og-preview-${item.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    title="Preview gambar OG untuk share ke sosial media"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    Preview OG
                  </button>
                )}
                <button
                  onClick={() => del(item.id)}
                  data-testid={`admin-${entity}-delete-${item.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form
            onSubmit={save}
            data-testid={`admin-${entity}-form`}
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h3 className="font-heading text-xl font-bold text-slate-900">
                {editing._new ? "Tambah" : "Edit"} {cfg.label}
              </h3>
              <button
                type="button"
                onClick={close}
                className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.name}>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block flex items-center gap-1.5">
                    {f.label}
                    {f.optional && (
                      <span className="text-[10px] font-normal text-slate-400">
                        (opsional)
                      </span>
                    )}
                  </label>
                  {f.type === "checkbox" ? (
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!form[f.name]}
                        onChange={(e) =>
                          setForm({ ...form, [f.name]: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-slate-300 text-blue-800 focus:ring-blue-200"
                      />
                      <span className="text-sm text-slate-600">
                        {form[f.name] ? "Ya" : "Tidak"}
                      </span>
                    </label>
                  ) : f.type === "icon" ? (
                    <div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const Ico = LucideIcons[form[f.name]] || LucideIcons.Users;
                          return (
                            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center shrink-0">
                              <Ico className="h-5 w-5" />
                            </div>
                          );
                        })()}
                        <input
                          list={`icon-suggest-${f.name}`}
                          value={form[f.name] ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, [f.name]: e.target.value })
                          }
                          placeholder={f.placeholder || "Users"}
                          required={!f.optional}
                          className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm font-mono"
                        />
                        <datalist id={`icon-suggest-${f.name}`}>
                          {ICON_SUGGESTIONS.map((n) => (
                            <option key={n} value={n} />
                          ))}
                        </datalist>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`icon-picker-${f.name}`}>
                        {ICON_SUGGESTIONS.slice(0, 18).map((n) => {
                          const Ico = LucideIcons[n];
                          const selected = form[f.name] === n;
                          return (
                            <button
                              key={n}
                              type="button"
                              title={n}
                              onClick={() => setForm({ ...form, [f.name]: n })}
                              className={`h-8 w-8 rounded-lg border flex items-center justify-center transition ${
                                selected
                                  ? "bg-blue-800 border-blue-800 text-white"
                                  : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-800"
                              }`}
                            >
                              <Ico className="h-4 w-4" />
                            </button>
                          );
                        })}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        Ketik nama Lucide icon apapun atau klik ikon di atas.
                        Daftar lengkap: <a href="https://lucide.dev/icons" target="_blank" rel="noreferrer" className="text-blue-700 underline">lucide.dev/icons</a>
                      </div>
                    </div>
                  ) : f.type === "textarea" ? (
                    <textarea
                      value={form[f.name] ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, [f.name]: e.target.value })
                      }
                      required={!f.optional}
                      rows={3}
                      placeholder={f.placeholder}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm resize-none"
                    />
                  ) : IMAGE_FIELD_NAMES.has(f.name) || f.type === "image" ? (
                    <ImageUploader
                      value={form[f.name] ?? ""}
                      onChange={(v) => setForm({ ...form, [f.name]: v })}
                      testid={`admin-${entity}-image-${f.name}`}
                      openLibrary={() => setLibraryFor(f.name)}
                      defaultAspect={DEFAULT_ASPECT[entity]?.[f.name] || "free"}
                    />
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={form[f.name] ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, [f.name]: e.target.value })
                      }
                      placeholder={f.placeholder}
                      required={!f.optional}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-slate-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={close}
                className="px-5 py-2.5 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid={`admin-${entity}-save`}
                className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-md shadow-blue-900/20"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Simpan
              </button>
            </div>
          </form>
        </div>
      )}

      {ogPreview && (
        <OgPreviewModal item={ogPreview} onClose={() => setOgPreview(null)} />
      )}

      <MediaLibraryModal
        open={!!libraryFor}
        onClose={() => setLibraryFor(null)}
        onPick={(url) => {
          if (libraryFor) setForm((f) => ({ ...f, [libraryFor]: url }));
        }}
      />
    </div>
  );
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OgPreviewModal = ({ item, onClose }) => {
  const [lang, setLang] = useState("id");
  const [imgKey, setImgKey] = useState(0); // force re-fetch on lang toggle / refresh
  const previewUrl = `${API_BASE}/og-image?news_id=${encodeURIComponent(item.id)}&lang=${lang}`;
  const cacheBustedSrc = `${previewUrl}&_=${imgKey}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(previewUrl);
      toast.success("Link OG image disalin");
    } catch {
      toast.error("Gagal menyalin link");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-news-og-preview-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">
              Preview OG Image
            </h3>
            <p className="text-xs text-slate-500 mt-1 line-clamp-1">
              {item.title_id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">
              Bahasa:
            </span>
            {["id", "en"].map((l) => (
              <button
                key={l}
                onClick={() => {
                  setLang(l);
                  setImgKey((k) => k + 1);
                }}
                data-testid={`admin-news-og-lang-${l}`}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  lang === l
                    ? "bg-blue-800 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {l === "id" ? "Indonesia" : "English"}
              </button>
            ))}
            <button
              onClick={() => setImgKey((k) => k + 1)}
              className="ml-auto text-xs text-slate-500 hover:text-blue-800 px-2 py-1"
              title="Muat ulang"
            >
              ↻ Refresh
            </button>
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
            <img
              key={imgKey + lang}
              src={cacheBustedSrc}
              alt="OG image preview"
              data-testid="admin-news-og-image"
              className="w-full h-auto block"
            />
          </div>

          <div className="text-xs text-slate-500 leading-relaxed">
            Gambar 1200×630 ini akan muncul saat link artikel di-share ke WhatsApp,
            Facebook, Twitter/X, LinkedIn. Kalau overflow atau kurang cantik, edit judul
            atau ringkasan artikel lalu klik <b>Refresh</b>.
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={copyLink}
              data-testid="admin-news-og-copy-link"
              className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white text-sm font-semibold px-4 py-2 rounded-full"
            >
              <Copy className="h-4 w-4" />
              Salin URL
            </button>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-news-og-open-tab"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-800 px-3 py-2"
            >
              <ExternalLink className="h-4 w-4" />
              Buka di Tab Baru
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrudPanel;
